import { array } from '@ember/helper';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';

import type { ValidationError } from 'webapp-types';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';

export interface Args {
	items?: ValidationError[];
	skipFormError?: boolean;
}

export default class RowErrorComponent extends Component<Args> {
	node: HTMLElement | null = null;

	get items() {
		return this.args.items || [];
	}

	get isError() {
		return this.items.length > 0;
	}

	get hasWarnings() {
		return this.items.some((item) => !!item.isWarning);
	}

	get hasErrors() {
		return this.items.some((item) => !item.isWarning);
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
		this.updateDom();
	}

	@action
	scheduleUpdateDom() {
		scheduleOnce('afterRender', this, this.updateDom);
	}

	@action
	updateDom() {
		const node = this.node;

		if (!node) {
			return;
		}

		const parentNode = node.parentElement;
		if (!parentNode) {
			return;
		}

		const hasErrors = this.hasErrors;
		const hasWarnings = this.hasWarnings;

		// toggle error class on parent
		parentNode.classList.toggle('errorClass', hasErrors);
		parentNode.classList.toggle('warningClass', hasWarnings && !hasErrors);

		if (!this.args.skipFormError) {
			// toggle error message - if we have text
			const formNode = parentNode.closest('.validationForm'),
				errorMsgNode = formNode?.querySelector('.errorMessage'); // FormError

			if (!formNode || !errorMsgNode) {
				return;
			}

			const shouldShow = hasErrors || hasWarnings;
			errorMsgNode.classList.toggle('hidden', !shouldShow);
			errorMsgNode.classList.toggle('shown', shouldShow);

			// Apply warning class to error message only when there are warnings but no errors
			if (hasWarnings && !hasErrors) {
				errorMsgNode.classList.add('warning');
			} else {
				errorMsgNode.classList.remove('warning');
			}
		}
	}

	<template>
		<div
			class={{joinClasses (array "row-error" (if @skipFormError "skipFormError"))}}
			{{didInsert this.setupView}}
			{{didUpdate this.scheduleUpdateDom @items}}
		></div>
	</template>
}
